import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import { toast } from 'react-toastify';
import { MdSave, MdClose, MdExpandMore, MdExpandLess } from 'react-icons/md';
import GoBack from 'components/actions/GoBack';
import { frenchAreas } from 'variables/industries';
import { useIndustries } from 'services/industryService';
import Select from 'react-select';

interface FormTemplate {
    name: string;
    industry?: string;
    subIndustry?: string;
    regions?: string[];
    minimumPrice?: number;
    borderColor: string;
    borderWidth: string;
    borderRadius: string;
    backgroundColor: string;
    textColor: string;
    labelColor: string;
    buttonBackgroundColor: string;
    buttonTextColor: string;
    inputBackgroundColor: string;
    inputBorderColor: string;
    inputTextColor: string;
    fontFamily?: string;
    fontSize?: string;
    ctaText?: string;
    isDefault: boolean;
}

const defaultTemplate: FormTemplate = {
    name: 'Nouveau Template',
    industry: '',
    subIndustry: '',
    regions: [],
    minimumPrice: undefined,
    borderColor: '#e5e7eb',
    borderWidth: '1px',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    textColor: '#1f2937',
    labelColor: '#374151',
    buttonBackgroundColor: '#4f46e5',
    buttonTextColor: '#ffffff',
    inputBackgroundColor: '#ffffff',
    inputBorderColor: '#d1d5db',
    inputTextColor: '#1f2937',
    fontFamily: 'inherit',
    fontSize: '16px',
    ctaText: 'Envoyer ma demande',
    isDefault: false,
};

const NewFormTemplate = () => {
    const navigate = useNavigate();
    const { industries: industriesData, loading: industriesLoading } = useIndustries();
    const [template, setTemplate] = useState<FormTemplate>(defaultTemplate);
    const [isLoading, setIsLoading] = useState(false);
    const [isCampaignExpanded, setIsCampaignExpanded] = useState(true);
    const [isDesignExpanded, setIsDesignExpanded] = useState(false);

    const handleCreate = async () => {
        if (!template.name.trim()) {
            toast.error('Le nom du template est requis');
            return;
        }

        setIsLoading(true);
        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + localStorage.getItem('token'),
                },
            };
            await axios.post(`${CONFIG.apiUrl}/form-template`, template, config);
            toast.success('Template créé avec succès');
            navigate('/admin/form-templates');
        } catch (error) {
            console.error('Error creating template:', error);
            toast.error('Erreur lors de la création du template');
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (field: keyof FormTemplate, value: any) => {
        setTemplate({ ...template, [field]: value });
    };

    const handleMultiChange = (updates: Partial<FormTemplate>) => {
        setTemplate({ ...template, ...updates });
    };

    const handleIndustryChange = (value: string) => {
        const updates: Partial<FormTemplate> = { industry: value };
        const selectedIndustry = industriesData.find(i => i.name === value);
        if (value && selectedIndustry && selectedIndustry.subIndustries.length > 0) {
            updates.subIndustry = selectedIndustry.subIndustries[0].name;
        } else {
            updates.subIndustry = '';
        }
        handleMultiChange(updates);
    };

    const renderColorInput = (
        label: string,
        value: string,
        onChange: (value: string) => void
    ) => (
        <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-navy-700 dark:text-white">{label}</label>
            <div className="flex items-center gap-3">
                <input
                    type="color"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="h-10 w-20 cursor-pointer rounded-lg border-2 border-gray-200"
                />
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    placeholder="#000000"
                />
            </div>
        </div>
    );

    if (industriesLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="text-xl font-bold text-navy-700 dark:text-white">
                    Chargement des industries...
                </div>
            </div>
        );
    }

    return (
        <div className="h-full w-full rounded-[20px] pb-8">
            <div className="mt-4 mb-4 flex">
                <GoBack />
            </div>

            {/* Campaign Parameters Card */}
            <Card extra="p-6 shadow-3xl rounded-[20px] border mb-6">
                <div 
                    className="flex items-center justify-between cursor-pointer mb-6"
                    onClick={() => setIsCampaignExpanded(!isCampaignExpanded)}
                >
                    <div>
                        <h2 className="text-xl font-bold text-navy-700 dark:text-white">
                            Paramètres de la campagne
                        </h2>
                        <p className="mt-1 text-sm text-gray-600">
                            Configurez les critères de ciblage et les paramètres de votre campagne
                        </p>
                    </div>
                    {isCampaignExpanded ? (
                        <MdExpandLess className="h-6 w-6 text-navy-700 dark:text-white flex-shrink-0" />
                    ) : (
                        <MdExpandMore className="h-6 w-6 text-navy-700 dark:text-white flex-shrink-0" />
                    )}
                </div>

                {isCampaignExpanded && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="col-span-full">
                        <InputField
                            id="name"
                            label="Nom du template"
                            value={template.name}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                handleChange('name', e.target.value);
                            }}
                            placeholder="Mon template personnalisé"
                        />
                    </div>

                    <div className="col-span-full md:col-span-1">
                        <label className="text-sm font-semibold text-gray-700 dark:text-white ml-1">
                            Industrie
                        </label>
                        <div className="mt-2.5 w-full rounded-xl border-2 border-gray-200 px-2.5 py-3 text-sm text-gray-600 outline-none focus-within:border-brand-500 hover:border-gray-300 transition-all duration-200 dark:!border-white/10 dark:!bg-navy-800">
                            <select
                                id="industry"
                                className="w-full bg-white dark:!bg-navy-800 outline-none cursor-pointer"
                                value={template.industry || ''}
                                onChange={(e) => handleIndustryChange(e.target.value)}
                            >
                                <option value="">Sélectionner une industrie</option>
                                {industriesData.map((ind) => (
                                    <option key={ind._id} value={ind.name}>
                                        {ind.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {template.industry && (
                        <div className="col-span-full md:col-span-1">
                            <label className="text-sm font-semibold text-gray-700 dark:text-white ml-1">
                                Sous-industrie
                            </label>
                            <div className="mt-2.5 w-full rounded-xl border-2 border-gray-200 px-2.5 py-3 text-sm text-gray-600 outline-none focus-within:border-brand-500 hover:border-gray-300 transition-all duration-200 dark:!border-white/10 dark:!bg-navy-800">
                                <select
                                    id="subIndustry"
                                    className="w-full bg-white dark:!bg-navy-800 outline-none cursor-pointer"
                                    value={template.subIndustry || (() => {
                                        const selectedIndustry = industriesData.find(i => i.name === template.industry);
                                        return selectedIndustry && selectedIndustry.subIndustries.length > 0 ? selectedIndustry.subIndustries[0].name : '';
                                    })()}
                                    onChange={(e) => handleChange('subIndustry', e.target.value)}
                                >
                                    {(() => {
                                        const selectedIndustry = industriesData.find(i => i.name === template.industry);
                                        return selectedIndustry?.subIndustries.map((item: any) => (
                                            <option key={item.name} value={item.name}>
                                                {item.name}
                                            </option>
                                        ));
                                    })()}
                                </select>
                            </div>
                        </div>
                    )}

                    <div className="col-span-full md:col-span-1">
                        <label className="text-sm font-semibold text-gray-700 dark:text-white ml-1">
                            Régions
                        </label>
                        <div className="mt-2.5 w-full">
                            <Select
                                value={(template.regions || []).map((region: string) => ({
                                    value: region,
                                    label: region,
                                }))}
                                isMulti
                                onChange={(items) => {
                                    const regions = items.map((item) => item.value);
                                    handleMultiChange({ regions });
                                }}
                                options={frenchAreas.map((area) => ({
                                    value: area,
                                    label: area,
                                }))}
                                className="w-full"
                                classNamePrefix="select"
                                placeholder="Sélectionner des régions"
                            />
                        </div>
                    </div>

                    <div className="col-span-full md:col-span-1">
                        <InputField
                            id="minimumPrice"
                            label="Prix minimum auquel je souhaite vendre le lead"
                            type="number"
                            value={template.minimumPrice?.toString() || ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value ? parseFloat(e.target.value) : undefined;
                                handleChange('minimumPrice', value as any);
                            }}
                            placeholder="Ex: 15"
                        />
                    </div>

                    {/* <div className="col-span-full flex items-center gap-3">
                        <input
                            type="checkbox"
                            id="isDefault"
                            checked={template.isDefault}
                            onChange={(e) => handleChange('isDefault', e.target.checked)}
                            className="h-5 w-5 cursor-pointer"
                        />
                        <label htmlFor="isDefault" className="text-sm font-medium text-navy-700 dark:text-white cursor-pointer">
                            Définir comme template par défaut
                        </label>
                    </div> */}
                    </div>
                )}
            </Card>

            {/* Design Card */}
            <Card extra="p-6 shadow-3xl rounded-[20px] border">
                <div 
                    className="flex items-center justify-between cursor-pointer mb-6"
                    onClick={() => setIsDesignExpanded(!isDesignExpanded)}
                >
                    <div>
                        <h2 className="text-xl font-bold text-navy-700 dark:text-white">
                            Design et personnalisation
                        </h2>
                        <p className="mt-1 text-sm text-gray-600">
                            Personnalisez l'apparence de votre formulaire de génération de leads
                        </p>
                    </div>
                    {isDesignExpanded ? (
                        <MdExpandLess className="h-6 w-6 text-navy-700 dark:text-white flex-shrink-0" />
                    ) : (
                        <MdExpandMore className="h-6 w-6 text-navy-700 dark:text-white flex-shrink-0" />
                    )}
                </div>

                {isDesignExpanded && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <h3 className="text-lg font-bold text-navy-700 dark:text-white">Conteneur</h3>
                            {renderColorInput('Couleur de fond', template.backgroundColor, (v) =>
                                handleChange('backgroundColor', v)
                            )}
                            {renderColorInput('Couleur de bordure', template.borderColor, (v) =>
                                handleChange('borderColor', v)
                            )}
                            <InputField
                                id="borderWidth"
                                label="Largeur de bordure"
                                value={template.borderWidth}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('borderWidth', e.target.value)}
                                placeholder="1px"
                            />
                            <InputField
                                id="borderRadius"
                                label="Arrondi des bords"
                                value={template.borderRadius}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('borderRadius', e.target.value)}
                                placeholder="12px"
                            />
                        </div>

                        <div className="space-y-4">
                            <h3 className="text-lg font-bold text-navy-700 dark:text-white">Texte</h3>
                            {renderColorInput('Couleur du texte', template.textColor, (v) =>
                                handleChange('textColor', v)
                            )}
                            {renderColorInput('Couleur des labels', template.labelColor, (v) =>
                                handleChange('labelColor', v)
                            )}
                            <InputField
                                id="fontFamily"
                                label="Police"
                                value={template.fontFamily || 'inherit'}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('fontFamily', e.target.value)}
                                placeholder="inherit"
                            />
                            <InputField
                                id="fontSize"
                                label="Taille de police"
                                value={template.fontSize || '16px'}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('fontSize', e.target.value)}
                                placeholder="16px"
                            />
                        </div>

                        <div className="space-y-4">
                            <h3 className="text-lg font-bold text-navy-700 dark:text-white">Champs</h3>
                            {renderColorInput('Couleur de fond', template.inputBackgroundColor, (v) =>
                                handleChange('inputBackgroundColor', v)
                            )}
                            {renderColorInput('Couleur de bordure', template.inputBorderColor, (v) =>
                                handleChange('inputBorderColor', v)
                            )}
                            {renderColorInput('Couleur du texte', template.inputTextColor, (v) =>
                                handleChange('inputTextColor', v)
                            )}
                        </div>

                        <div className="space-y-4">
                            <h3 className="text-lg font-bold text-navy-700 dark:text-white">Bouton</h3>
                            {renderColorInput('Couleur de fond', template.buttonBackgroundColor, (v) =>
                                handleChange('buttonBackgroundColor', v)
                            )}
                            {renderColorInput('Couleur du texte', template.buttonTextColor, (v) =>
                                handleChange('buttonTextColor', v)
                            )}
                            <InputField
                                id="ctaText"
                                label="Texte du bouton CTA"
                                value={template.ctaText || ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('ctaText', e.target.value)}
                                placeholder="Envoyer ma demande"
                            />
                        </div>
                    </div>
                )}
            </Card>

            {/* Action Buttons */}
            <div className="mt-6 flex gap-3 justify-end">
                <button
                    onClick={() => navigate('/admin/form-templates')}
                    className="flex items-center gap-2 rounded-xl bg-gray-200 px-5 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-300"
                    disabled={isLoading}
                >
                    <MdClose className="h-5 w-5" />
                    Annuler
                </button>
                <button
                    onClick={handleCreate}
                    className="flex items-center gap-2 rounded-xl bg-brand-500 px-5 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isLoading}
                >
                    <MdSave className="h-5 w-5" />
                    {isLoading ? 'Création...' : 'Créer'}
                </button>
            </div>
        </div>
    );
};

export default NewFormTemplate;
